/**
 * Brand stack — scroll recede effect.
 *
 * Each card is sticky. As the next card rises to cover it, the covered card
 * scales down and dims slightly so it reads as receding behind the new one.
 *
 * Notes:
 *  - No scroll hijacking. Native scrolling is untouched.
 *  - Disabled below 820px, in the theme editor, and under reduced-motion.
 *  - Re-initialises on Shopify section events so the customiser stays live.
 */
(function () {
  'use strict';

  var DESKTOP = '(min-width: 821px)';
  var REDUCED = '(prefers-reduced-motion: reduce)';

  function BrandStack(root) {
    this.root = root;
    this.cards = Array.prototype.slice.call(root.querySelectorAll('.brand-card'));
    this.scale = (parseFloat(root.dataset.scale) || 0) / 100;
    this.dim = (parseFloat(root.dataset.dim) || 0) / 100;
    this.mobileStack = root.dataset.mobileStack !== 'false';
    this.ticking = false;
    this.lastWidth = window.innerWidth;

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.update = this.update.bind(this);

    this.cards.forEach(function (card, i) {
      card.style.zIndex = String(i + 1);
    });

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    this.update();
  }

  BrandStack.prototype.enabled = function () {
    if (window.matchMedia(REDUCED).matches) return false;
    if (document.body.classList.contains('shopify-design-mode')) return false;

    // Below the breakpoint, only run if mobile stacking is switched on
    if (!window.matchMedia(DESKTOP).matches) return this.mobileStack;

    return true;
  };

  /**
   * Mobile browsers fire resize when the toolbar collapses during scroll.
   * Recalculating on those events causes visible jumps, so ignore any
   * resize where the width hasn't actually changed.
   */
  BrandStack.prototype.onResize = function () {
    if (window.innerWidth === this.lastWidth) return;
    this.lastWidth = window.innerWidth;
    this.onScroll();
  };

  BrandStack.prototype.onScroll = function () {
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(this.update);
  };

  BrandStack.prototype.update = function () {
    var on = this.enabled();
    var vh = window.innerHeight;
    var cards = this.cards;
    var self = this;

    cards.forEach(function (card, i) {
      var inner = card.querySelector('.brand-card__inner');
      if (!inner) return;

      var next = cards[i + 1];

      if (!on || !next) {
        inner.style.transform = '';
        inner.style.filter = '';
        return;
      }

      // 0 = next card still below the fold, 1 = next card fully covering
      var top = next.getBoundingClientRect().top;
      var p = 1 - Math.min(Math.max(top / vh, 0), 1);

      // Small screens get a gentler recede; a 6% shrink reads as a glitch there
      var scale = window.matchMedia(DESKTOP).matches ? self.scale : self.scale * 0.5;

      inner.style.transform = 'scale(' + (1 - scale * p).toFixed(4) + ')';
      inner.style.filter = 'brightness(' + (1 - self.dim * p).toFixed(4) + ')';
    });

    this.ticking = false;
  };

  BrandStack.prototype.destroy = function () {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
  };

  var instances = {};

  function init(scope) {
    (scope || document).querySelectorAll('.brand-stack').forEach(function (root) {
      var id = root.dataset.sectionId;
      if (instances[id]) instances[id].destroy();
      instances[id] = new BrandStack(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Keep the customiser in sync
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('.brand-stack');
    if (root && instances[root.dataset.sectionId]) {
      instances[root.dataset.sectionId].destroy();
      delete instances[root.dataset.sectionId];
    }
  });

  document.addEventListener('shopify:block:select', function (e) {
    if (e.target.classList && e.target.classList.contains('brand-card')) {
      e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });
})();
